import React, { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";

const OTRA = "otra cosa";

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`rounded-xl px-4 py-2.5 text-sm border transition-all ${active ? "border-dorado text-dorado bg-[rgba(201,168,76,0.08)]" : "border-borde text-gris hover:text-crema hover:border-gris"}`}>
    {children}
  </button>
);

export default function Encuesta({ procesoId, pasos = [], onDone, onSkip }) {
  const [dificultad, setDificultad] = useState(null);
  const [pudoSolo, setPudoSolo] = useState(null); // true | false
  const [donde, setDonde] = useState(null);
  const [detalle, setDetalle] = useState("");
  const [enviando, setEnviando] = useState(false);

  const necesitaDonde = pudoSolo === false;
  const dondeOk = !necesitaDonde || (donde && (donde !== OTRA || detalle.trim().length > 0));
  const completo = dificultad != null && pudoSolo != null && dondeOk;

  const enviar = async () => {
    if (!completo || enviando) return;
    setEnviando(true);
    try {
      await api.post("/encuesta", {
        proceso_id: procesoId,
        dificultad,
        pudo_solo: pudoSolo,
        donde_traba: necesitaDonde ? donde : null,
        detalle: necesitaDonde && donde === OTRA ? detalle.trim() : "",
      });
      setEnviando(false);
      onDone && onDone();
    } catch (e) {
      setEnviando(false);
      toast.error("No pude guardar tu respuesta. Probá de nuevo.");
    }
  };

  const saltear = async () => {
    if (enviando) return;
    setEnviando(true);
    try { await api.post("/encuesta/saltear", { proceso_id: procesoId }); }
    catch (e) { console.warn("No se pudo saltear la encuesta:", e); }
    setEnviando(false);
    onSkip && onSkip();
  };

  return createPortal(
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 z-[10000] flex flex-col items-center px-6 overflow-y-auto py-16"
      style={{ background: "#0A0A0A" }} data-testid="encuesta">
      <div className="max-w-lg w-full my-auto">
        <div className="mono-label text-dorado mb-2">ANTES DE SEGUIR</div>
        <h2 className="titulo text-3xl text-crema mb-8">Tres preguntas cortas.</h2>

        {/* 1 · dificultad */}
        <div className="mb-8">
          <p className="text-crema mb-3">¿Qué tan complicado te resultó?</p>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => setDificultad(n)} data-testid={`enc-dificultad-${n}`}
                className={`w-12 h-12 rounded-full border text-lg font-semibold transition-all ${dificultad === n ? "border-dorado text-dorado bg-[rgba(201,168,76,0.1)]" : "border-borde text-gris hover:text-crema"}`}>
                {n}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-gris text-xs mt-1.5" style={{ maxWidth: 300 }}>
            <span>muy fácil</span><span>muy difícil</span>
          </div>
        </div>

        {/* 2 · lo pudo hacer solo */}
        <div className="mb-8">
          <p className="text-crema mb-3">¿Lo pudiste hacer solo?</p>
          <div className="flex gap-2">
            <Chip active={pudoSolo === true} onClick={() => setPudoSolo(true)} testid="enc-solo-si">Sí</Chip>
            <Chip active={pudoSolo === false} onClick={() => setPudoSolo(false)} testid="enc-solo-no">No</Chip>
          </div>
        </div>

        {/* 3 · dónde te trabaste (solo si contestó que no) */}
        <AnimatePresence>
          {necesitaDonde && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
              className="mb-9 overflow-hidden" data-testid="enc-donde-bloque">
              <p className="text-crema mb-3">¿Dónde te trabaste?</p>
              <div className="flex flex-wrap gap-2">
                {pasos.map((p, i) => (
                  <Chip key={i} active={donde === p} onClick={() => setDonde(p)} testid={`enc-donde-${i}`}>{p}</Chip>
                ))}
                <Chip active={donde === OTRA} onClick={() => setDonde(OTRA)} testid="enc-donde-otra">Otra cosa</Chip>
              </div>
              {donde === OTRA && (
                <textarea className="input-dark min-h-[70px] mt-3" value={detalle} onChange={(e) => setDetalle(e.target.value)}
                  placeholder="Contame dónde te trabaste…" data-testid="enc-donde-detalle" />
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-center gap-4">
          <button className="btn-dorado" disabled={!completo || enviando} onClick={enviar} data-testid="enc-enviar">LISTO</button>
          <button className="mono-label text-gris hover:text-crema underline transition-colors" disabled={enviando} onClick={saltear} data-testid="enc-saltear">ahora no</button>
        </div>
      </div>
    </motion.div>,
    document.body
  );
}
